import { useAuth0 } from '@auth0/auth0-react';
import { DirectReportsType, EmploymentTypes, WorkArrangements, WorkExperienceType } from '@talentmesh/utils';
import useSWRMutation from 'swr/mutation';
import { RecruiterAssessmentDTO } from '@/DTO/Assessments/RecruiterAssessmentDTO';
import JobAdSkillsType from '@/Models/JobAdSkillsType';
import LocationDetails from '@/Models/LocationDetails';
import http from '@/Utils/HTTP/NewHttpUtils';

export interface ValidateOneClickRequest {
    url: string;
}

export type OneClickCreateRecruitmentOutcome = 'Created' | 'Fallback';

export interface JobDetailsPrefillResponse {
    name: string;
    locationDetails: LocationDetails | null;
    jobFunctionId: number | null;
    workExperience: WorkExperienceType;
    directReports: DirectReportsType;
    employmentType: EmploymentTypes;
    workArrangement: WorkArrangements;
    yearlySalaryFrom: number | null;
    yearlySalaryTo: number | null;
    currency: string | null;
    jobDescriptionLanguage: string;
}

interface JobAdSkillsDTO {
    skillName: string;
    skillType: JobAdSkillsType;
    isUserDefined: boolean;
}

interface JobAdPrefillResponse {
    title: string;
    description: string;
    skills: JobAdSkillsDTO[];
    companyDescription: string;
}

export interface ExperienceCriteriaPrefillResponse {
    criterion: string;
}

/**
 * The assembled recruitment data returned on a validation fallback so the manual wizard can be pre-filled.
 */
export interface OneClickRecruitmentPrefillResponse {
    jobDetails: JobDetailsPrefillResponse;
    jobAd: JobAdPrefillResponse;
    experienceCriteria: Array<ExperienceCriteriaPrefillResponse>;
}

/**
 * The `outcome` field discriminates the variant:
 *  - `Created`  — recruitment created; `recruitment` is populated.
 *  - `Fallback` — crawl succeeded but the result isn't a valid recruitment; complete it manually. `prefill` carries the assembled data.
 */
export interface OneClickCreateRecruitmentResponse {
    outcome: OneClickCreateRecruitmentOutcome;
    recruitment: RecruiterAssessmentDTO | null;
    prefill: OneClickRecruitmentPrefillResponse | null;
    fallbackReason: string | null;
}

const useOneClickCreateRecruitment = (clientId: string) => {
    const { getAccessTokenSilently } = useAuth0();

    return useSWRMutation(
        `recruitments/client/${clientId}/one-click-create`,
        async (path: string, { arg }: { arg: ValidateOneClickRequest }) => {
            return http.post<OneClickCreateRecruitmentResponse>({
                path,
                accessToken: await getAccessTokenSilently(),
                body: arg,
            });
        },
        {
            // override global error handler.
            // there is a specific error handling for this cases
            onError: () => {},
        },
    );
};

export default useOneClickCreateRecruitment;
